import { FC, useState, useEffect } from 'react';

import { useNotificationContext } from 'application/providers/NotificationProvider';
import { TEXTS } from 'application/pages/AdsPage/renderer/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';

import useAdCampaignUpdate from 'domain/media/useAdCampaignUpdate';
import { type AdCampaignPureType, useMediaAdCampaignQuery } from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';

import { NOTIFICATIONS, NAME_MAX_LENGTH_ERROR } from '../consts';
import styles from './index.module.scss';

const AdRenameModal: FC<ModalProps[ModalTypes.AD_CAMPAIGN_RENAME]> = ({ id, close }) => {
  const notification = useNotificationContext();
  const [updateAdCampaign, { loading }] = useAdCampaignUpdate();

  const { data, loading: campaignLoading } = useMediaAdCampaignQuery({
    variables: { adCampaignId: id },
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const originalTitle = data?.adCampaignPure?.title ?? '';

  const [name, setName] = useState(originalTitle);

  useEffect(() => {
    if (originalTitle) {
      setName(originalTitle);
    }
  }, [id, originalTitle]);

  const isRenameButtonDisabled = campaignLoading || name.length === 0 || name === originalTitle;

  const onRename = async () => {
    try {
      const ok = await updateAdCampaign({ ...(data!.adCampaignPure as AdCampaignPureType), title: name });

      if (!ok) throw new Error();

      notification.showNotification(NOTIFICATIONS.RENAME_SUCCESS);
      close();
    } catch (error: any) {
      if (JSON.stringify(error).includes(NAME_MAX_LENGTH_ERROR)) {
        notification.showNotification(NOTIFICATIONS.MAX_LENGTH_ERROR);
      } else {
        notification.showNotification(NOTIFICATIONS.ERROR);
      }
    }
  };

  return (
    <ModalScreen
      title={TEXTS.MODAL_AD_RENAME_TITLE}
      titlePlacement="left"
      onClose={close}
      actionButtons={[
        <Button
          key="button_rename_ad_campaign_modal"
          elementId="button_rename_ad_campaign_modal"
          type={ButtonType.PRIMARY}
          isDisabled={isRenameButtonDisabled}
          onClick={onRename}
          isLoading={loading}>
          {TEXTS.MODAL_AD_RENAME_BUTTON_CONFIRM}
        </Button>,
      ]}>
      <Input
        elementId="input_rename-campaign"
        label={TEXTS.MODAL_AD_RENAME_INPUT_LABEL}
        isDisabled={loading || campaignLoading}
        labelClassName={styles.label}
        type={InputType.TEXT}
        value={name}
        onChange={setName}
      />
    </ModalScreen>
  );
};

export default AdRenameModal;
